import type { ViewController } from '@presentation/shell/NexxusShell';
import type { CentreAnalytique } from '@shared/types/CentreAnalytique';
import { AgregationAnalytique } from '@domain/analytique/AgregationAnalytique';
import { fmtFCFA, esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { canMutateComptaRecords } from '@application/access/ViewAccessPolicy';
import { uuid } from '@shared/utils/uuid';
import {
  bindCrudTable,
  crudActions,
  emptyRow,
  openRegisterForm,
} from '@presentation/shell/localCrud';

export const analytiqueController: ViewController = async (section, ctx, shell) => {
  let centres = await ctx.centresAnalytiques.list();
  const ecritures = await ctx.ecritures.list();
  const canMutate = canMutateComptaRecords(shell.permission);
  const tenantId = ctx.tenantContext.tenantId();
  const byCode = (): Map<string, CentreAnalytique> => new Map(centres.map((c) => [c.code, c]));
  const periodes = Array.from(new Set(ecritures.map((e) => e.periode))).sort();
  const annees = Array.from(new Set(periodes.map((p) => p.slice(0, 4)))).sort();

  const renderRef = (): void => {
    const refBody = section.querySelector('#ana-table-cdc tbody');
    if (!refBody) return;
    refBody.innerHTML =
      centres.length === 0
        ? emptyRow(6, 'Aucun centre analytique. Créez-en un.')
        : centres
            .map(
              (c: CentreAnalytique) => `<tr>
          <td class="mono">${esc(c.code)}</td>
          <td>${esc(c.libelle)}</td>
          <td><span class="pill ${c.type === 'projet' ? 'cyan' : 'info'}">${c.type === 'projet' ? 'Projet' : 'Centre de coût'}</span></td>
          <td class="num">${esc(fmtFCFA(c.budget_annuel))}</td>
          <td class="num">${c.actif ? '<span class="pill success">Actif</span>' : '<span class="pill">Inactif</span>'}</td>
          <td class="num">${crudActions(c.id)}</td>
        </tr>`,
            )
            .join('');
  };
  renderRef();

  // SIG (par période)
  const sigSel = section.querySelector<HTMLSelectElement>('#ana-sig-periode');
  if (sigSel && sigSel.options.length === 0) {
    sigSel.innerHTML =
      '<option value="">Toutes périodes</option>' +
      periodes.map((p) => `<option value="${p}">${p}</option>`).join('');
  }
  const renderSIG = (): void => {
    const per = sigSel?.value ?? '';
    const rows = per ? ecritures.filter((e) => e.periode === per) : ecritures;
    const agg = [...AgregationAnalytique.agreger(rows).values()];
    const tbody = section.querySelector('#ana-table-sig tbody');
    if (!tbody) return;
    tbody.innerHTML =
      agg.length === 0
        ? '<tr><td colspan="5" style="color:var(--gris);padding:14px;">Aucune donnée analytique (axe non renseigné sur les écritures).</td></tr>'
        : (() => {
            const totalAbs = agg.reduce((s, a) => s + Math.abs(a.resultat), 0) || 1;
            return agg
              .map((a) => {
                const pct = Math.round((Math.abs(a.resultat) / totalAbs) * 100);
                return `<tr>
          <td>${esc(byCode().get(a.code)?.libelle ?? a.code)}</td>
          <td class="num">${esc(fmtFCFA(a.produits))}</td>
          <td class="num">${esc(fmtFCFA(a.charges))}</td>
          <td class="num" style="font-weight:700;color:${a.resultat >= 0 ? 'var(--teal)' : 'var(--danger)'};">${esc(fmtFCFA(a.resultat))}</td>
          <td class="num">${pct} %</td>
        </tr>`;
              })
              .join('');
          })();
  };
  renderSIG();

  // Marge par projet
  const margeBody = section.querySelector('#ana-table-marge tbody');
  if (margeBody) {
    const projets = centres.filter((c) => c.type === 'projet');
    const agg = AgregationAnalytique.agreger(ecritures);
    margeBody.innerHTML =
      projets.length === 0
        ? '<tr><td colspan="5" style="color:var(--gris);padding:14px;">Aucun projet analytique.</td></tr>'
        : projets
            .map((p) => {
              const a = agg.get(p.code) ?? { produits: 0, charges: 0, resultat: 0 };
              const taux =
                a.produits > 0
                  ? Math.round((a.resultat / a.produits) * 100)
                  : a.charges > 0
                    ? -100
                    : 0;
              return `<tr>
                <td>${esc(p.libelle)}</td>
                <td class="num">${esc(fmtFCFA(a.produits))}</td>
                <td class="num">${esc(fmtFCFA(a.charges))}</td>
                <td class="num">${esc(fmtFCFA(a.resultat))}</td>
                <td class="num">${taux} %</td>
              </tr>`;
            })
            .join('');
  }

  // Budget vs réel
  const budgetSelAnnee = section.querySelector<HTMLSelectElement>('#ana-budget-annee');
  if (budgetSelAnnee && budgetSelAnnee.options.length === 0) {
    budgetSelAnnee.innerHTML = (annees.length ? annees : [String(new Date().getFullYear())])
      .map((y) => `<option value="${y}">${y}</option>`)
      .join('');
  }
  const renderBudget = (): void => {
    const annee = budgetSelAnnee?.value ?? '';
    const typeF = section.querySelector<HTMLSelectElement>('#ana-budget-type')?.value ?? 'all';
    const rows = ecritures.filter((e) => !annee || e.periode.startsWith(annee));
    const agg = AgregationAnalytique.agreger(rows);
    const cible = centres.filter((c) => typeF === 'all' || c.type === typeF);
    const tbody = section.querySelector('#ana-table-budget tbody');
    if (!tbody) return;
    tbody.innerHTML =
      cible.length === 0
        ? '<tr><td colspan="7" style="color:var(--gris);padding:14px;">Aucun centre pour ce filtre.</td></tr>'
        : cible
            .map((c) => {
              const budget = (c.budgets_par_annee?.[annee] ?? c.budget_annuel) || 0;
              const reel = Math.abs(agg.get(c.code)?.charges ?? 0);
              const ecart = budget - reel;
              const pct = budget ? Math.round((reel / budget) * 100) : 0;
              const etat =
                pct > 100 ? 'Dépassé' : pct >= 90 ? 'Alerte' : budget > 0 ? 'OK' : 'Sans budget';
              return `<tr>
          <td>${esc(c.libelle)}</td>
          <td>${c.type === 'projet' ? 'Projet' : 'CdC'}</td>
          <td class="num">${esc(fmtFCFA(budget))}</td>
          <td class="num">${esc(fmtFCFA(reel))}</td>
          <td class="num" style="color:${ecart >= 0 ? 'var(--success)' : 'var(--danger)'};">${esc(fmtFCFA(ecart))}</td>
          <td class="num">${pct} %</td>
          <td><span class="pill ${pct > 100 ? 'warning' : 'success'}">${esc(etat)}</span></td>
        </tr>`;
            })
            .join('');
  };
  renderBudget();

  const w = window as unknown as Record<string, unknown>;
  w['switchAnaTab'] = (name: string) => {
    section.querySelectorAll<HTMLElement>('.tab-panel').forEach((p) => {
      p.style.display = p.id === `ana-panel-${name}` ? '' : 'none';
    });
    section.querySelectorAll<HTMLElement>('#ana-tabs .tab-btn').forEach((b) => {
      b.classList.toggle('active', b.getAttribute('data-on-click')?.includes(`'${name}'`) ?? false);
    });
  };
  w['renderAnaSIG'] = () => renderSIG();
  w['renderAnaBudget'] = () => renderBudget();
  const openEditor = (existing?: CentreAnalytique): void => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier le centre' : 'Nouveau centre',
      fields: [
        { kind: 'text', name: 'code', label: 'Code', required: true, placeholder: 'CDC-ADM' },
        { kind: 'text', name: 'libelle', label: 'Libellé', required: true },
        {
          kind: 'select',
          name: 'type',
          label: 'Type',
          options: [
            { value: 'cdc', label: 'Centre de coût' },
            { value: 'projet', label: 'Projet' },
          ],
        },
        { kind: 'number', name: 'budget_annuel', label: 'Budget annuel', min: 0 },
      ],
      values: existing
        ? {
            code: existing.code,
            libelle: existing.libelle,
            type: existing.type,
            budget_annuel: existing.budget_annuel,
          }
        : undefined,
      onSubmit: async (data) => {
        const now = new Date().toISOString();
        const row: CentreAnalytique = {
          id: existing?.id ?? uuid('cdc'),
          tenant_id: existing?.tenant_id ?? tenantId,
          code: String(data['code']),
          libelle: String(data['libelle']),
          type: data['type'] === 'projet' ? 'projet' : 'cdc',
          budget_annuel: Number(data['budget_annuel'] ?? 0),
          actif: existing?.actif ?? true,
          cree_le: existing?.cree_le ?? now,
          modifie_le: now,
        };
        if (existing) await ctx.centresAnalytiques.update(row);
        else await ctx.centresAnalytiques.add(row);
        centres = await ctx.centresAnalytiques.list();
        renderRef();
      },
    });
  };
  bindCrudTable(
    section.querySelector('#ana-table-cdc tbody'),
    section,
    (id) => openEditor(centres.find((c) => c.id === id)),
    async (id) => {
      await ctx.centresAnalytiques.delete(id);
      centres = await ctx.centresAnalytiques.list();
      renderRef();
    },
    canMutate,
  );
  w['openCdcModal'] = () => openEditor();
  w['seedCentresAnalytiquesDemo'] = () => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    void import('@infra/licence/demoSeedConfig').then(({ isDemoSeedEnabled }) => {
      if (!isDemoSeedEnabled()) {
        ToastService.warning('Les données de démonstration sont désactivées sur cette version.');
        return;
      }
      void (async () => {
        const demo: Array<Pick<CentreAnalytique, 'code' | 'libelle' | 'type' | 'budget_annuel'>> = [
          { code: 'CDC-ADM', libelle: 'Administration', type: 'cdc', budget_annuel: 12_000_000 },
          { code: 'CDC-COM', libelle: 'Commercial', type: 'cdc', budget_annuel: 8_000_000 },
          { code: 'CDC-PROD', libelle: 'Production', type: 'cdc', budget_annuel: 20_000_000 },
          { code: 'CDC-RH', libelle: 'Ressources humaines', type: 'cdc', budget_annuel: 6_000_000 },
          { code: 'CDC-IT', libelle: 'Informatique', type: 'cdc', budget_annuel: 5_000_000 },
          { code: 'PRJ-PILOTE', libelle: 'Projet pilote', type: 'projet', budget_annuel: 3_000_000 },
        ];
        const existingCodes = new Set(centres.map((c) => c.code));
        let added = 0;
        const now = new Date().toISOString();
        for (const d of demo) {
          if (existingCodes.has(d.code)) continue;
          await ctx.centresAnalytiques.add({
            id: uuid('cdc'),
            tenant_id: tenantId,
            ...d,
            actif: true,
            cree_le: now,
          });
          added += 1;
        }
        centres = await ctx.centresAnalytiques.list();
        renderRef();
        ToastService.success(
          added ? `${added} centre(s) de démonstration créé(s)` : 'Centres déjà présents',
        );
      })();
    });
  };
  w['seedAnalytiqueMultiExercices'] = () =>
    ToastService.info(
      'Le seed multi-exercices (ré-imputation des écritures) n’est pas activé — créez les centres à la main.',
    );
};
